import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { MaterialDataTable } from '../../table-common/MaterialDataTable';
import { RuleRunsTable } from './RuleRunsTable';
import { useRuleRuns } from './useRuleRuns';
import { formatAlarmInterval } from './alarm-interval';
import {
  dataWindowSecondsToValue,
  dataWindowUnitLabels,
  formatDataWindowValue,
  getAlarmRuleErrorMessage,
  getDataWindowUnit,
} from '../filterParameterMapping';

const getTriggeredAlarmColumns = formatTimestampFilter => [
  { accessorKey: 'id', header: 'ID', size: 80 },
  { accessorKey: 'name', header: 'Name', size: 180 },
  { accessorKey: 'occurrenceTotalCount', header: 'Occurrence Total Count', size: 180 },
  { accessorKey: 'triggeredCount', header: 'Triggered Count', size: 130 },
  ...[['createdTs', 'Created'], ['updatedTs', 'Updated']].map(([id, header]) => ({
    accessorKey: id,
    header,
    size: 220,
    Cell: ({ row }) => row.original[id] ? formatTimestampFilter(row.original[id]) : '-',
  })),
  { id: 'isHandled', header: 'Handled', size: 100, accessorFn: alarm => alarm.isHandled ? 'Yes' : 'No' },
];

export const AlarmRuleViewModal = ({ rule, onClose, alarmRuleService, accountService, alarmNotificationService, formatTimestampFilter, $translate }) => {
  const [summary, setSummary] = useState(rule);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(null);
  const [summaryVersion, setSummaryVersion] = useState(0);
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [runsActivated, setRunsActivated] = useState(false);
  const mountedRef = useRef(false);
  const { cancel: cancelRuns, restart: restartRuns, ...runs } = useRuleRuns(rule.id, alarmRuleService, runsActivated, activeTab === 1);
  const alarmEvents = summary.alarmEvents || [];

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setSummaryLoading(true);
    setSummaryError(null);
    const load = async () => {
      try {
        const result = await alarmRuleService.getSummary(rule.id);
        if (!cancelled && mountedRef.current) setSummary(result);
      } catch (error) {
        if (cancelled || !mountedRef.current) return;
        const message = getAlarmRuleErrorMessage(error, 'Rule details could not be loaded. Please try again.');
        setSummaryError(message);
        alarmNotificationService.error('Failed to load rule details', message);
      } finally {
        if (!cancelled && mountedRef.current) setSummaryLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [rule.id, alarmRuleService, alarmNotificationService, summaryVersion]);

  const hasSubscribers = !!summary.subscribedUserIds?.length;
  useEffect(() => {
    if (!hasSubscribers) return;
    let cancelled = false;
    accountService.getAll().then(result => {
      if (!cancelled && mountedRef.current) setUsers(result || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [hasSubscribers, accountService]);

  const usersById = new Map(users.map(user => [Number(user.id), user]));
  const subscriberIds = (summary.subscribedUserIds || []).map(Number);
  const subscriberText = subscriberIds.length
    ? subscriberIds.map(id => usersById.get(id)?.name || `User ID: ${id}`).join('\n')
    : 'None';
  const unit = getDataWindowUnit(summary.dataWindowSeconds);
  const dataWindow = formatDataWindowValue(dataWindowSecondsToValue(summary.dataWindowSeconds, unit));
  const close = () => {
    mountedRef.current = false;
    cancelRuns();
    onClose();
  };

  return (
    <div className="alarm-rule-view-modal" data-testid="alarm-rule-view-modal">
      <div className="modal-header">
        <h3 className="modal-title">Rule for {summary.ruleName}</h3>
        <button type="button" className="close" aria-label="close" onClick={close} data-testid="alarm-rule-view-header-close-button">
          <span aria-hidden="true">x</span>
        </button>
      </div>
      <div className="modal-body" data-testid="alarm-rule-view-modal-body">
        <p className="help-block" data-testid="alarm-rule-view-data-window-help">Rules are evaluated every hour.</p>
        {summaryLoading && <output className="help-block" data-testid="alarm-rule-view-summary-loading">Loading rule details...</output>}
        {summaryError && (
          <div className="alert alert-danger" role="alert" data-testid="alarm-rule-view-summary-error">
            <p>{summaryError}</p>
            <button type="button" className="btn btn-secondary" disabled={summaryLoading}
              onClick={() => setSummaryVersion(version => version + 1)} data-testid="alarm-rule-view-summary-retry-button">
              <i className="fas fa-sync-alt fa-fw" aria-hidden="true" /> Retry
            </button>
          </div>
        )}
        <div className="alarm-rule-view-modal__meta" data-testid="alarm-rule-view-modal-meta">
          <p><strong>Rule ID:</strong> {summary.id}</p>
          <p><strong>Description:</strong> {summary.description || '-'}</p>
          <p><strong>Type:</strong> {summary.ruleType || '-'}</p>
          <p><strong>Owner:</strong> {summary.ownerText || 'Unassigned'}</p>
          <p><strong>Consists:</strong> {(summary.consists || []).join(', ') || '-'}</p>
          <p><strong>Cars:</strong> {(summary.cars || []).join(', ') || '-'}</p>
          <p><strong>Event IDs:</strong> {Array.isArray(summary.eventNumbers) ? summary.eventNumbers.join(',') : summary.eventNumbers ?? '-'}</p>
          <p><strong>Threshold:</strong> {summary.alarmThresholdCount} in {formatAlarmInterval(summary.alarmIntervalSeconds)}</p>
          <p><strong>Status:</strong> {summary.archivedTs ? 'Archived and inactive' : summary.isActive ? 'Active' : 'Inactive'}</p>
          <p><strong>Data Window:</strong> {dataWindow} {dataWindowUnitLabels[unit]}</p>
          <p><strong>Created:</strong> {formatTimestampFilter(summary.createdTs || summary.created_ts || summary.created)}</p>
        </div>
        <div className="form-group">
          <label htmlFor="alarmRuleViewSubscribers">Subscribers:</label>
          <textarea id="alarmRuleViewSubscribers" className="form-control" rows="3" readOnly value={subscriberText} data-testid="alarm-rule-view-subscribers" />
        </div>
        <div className="alarm-view-modal-tabs" data-testid="alarm-rule-view-tabs">
          <ul className="nav nav-tabs nav-justified" role="tablist">
            <li className={activeTab === 0 ? 'active' : ''} role="presentation" data-testid="alarm-rule-view-triggered-tab-item">
              <a href="#alarm-rule-triggered-panel" id="alarm-rule-triggered-tab" role="tab" aria-controls="alarm-rule-triggered-panel"
                aria-selected={activeTab === 0} data-testid="alarm-rule-view-triggered-alarms-tab"
                onClick={event => { event.preventDefault(); cancelRuns(); setActiveTab(0); }}>
                {$translate.instant('alarm-rule-view.triggered-alarms-tab')}
              </a>
            </li>
            <li className={activeTab === 1 ? 'active' : ''} role="presentation" data-testid="alarm-rule-view-runs-tab-item">
              <a href="#alarm-rule-runs-panel" id="alarm-rule-runs-tab" role="tab" aria-controls="alarm-rule-runs-panel"
                aria-selected={activeTab === 1} data-testid="alarm-rule-view-rule-runs-tab"
                onClick={event => { event.preventDefault(); restartRuns(); setRunsActivated(true); setActiveTab(1); }}>
                {$translate.instant('alarm-rule-view.rule-runs-tab')}
              </a>
            </li>
          </ul>
          <div className="tab-content">
            <div className={`tab-pane${activeTab === 0 ? ' active' : ''}`} id="alarm-rule-triggered-panel" role="tabpanel"
              aria-labelledby="alarm-rule-triggered-tab" data-testid="alarm-rule-view-triggered-alarms-panel">
              <div className="nav-content-custom" data-testid="alarm-rule-view-triggered-alarms-container">
                {summaryLoading && <p data-testid="alarm-rule-view-triggered-alarms-loading"><i className="fas fa-spinner fa-pulse fa-fw" /> Loading...</p>}
                {!summaryError && (
                  <MaterialDataTable
                    className="event-history-container consist-event-history alarm-rule-view-modal__alarms"
                    tableTestId="alarm-rule-view-triggered-alarms-table"
                    scrollContainerClassName="alarm-rule-view-modal__alarms-scroll"
                    ariaLabel="Triggered alarms"
                    columns={getTriggeredAlarmColumns(formatTimestampFilter)}
                    data={alarmEvents}
                    getRowId={alarm => String(alarm.id)}
                    isLoading={summaryLoading}
                    enableSorting={false}
                    enableTopToolbar
                    topToolbarActions={(
                      <span data-testid="alarm-rule-view-total-alarms" aria-live="polite">
                        <strong>Total Alarms:</strong> {summaryLoading ? '-' : alarmEvents.length.toLocaleString()}
                      </span>
                    )}
                    muiTableBodyRowProps={({ row }) => ({
                      'data-testid': `alarm-rule-view-triggered-alarm-row-${row.original.id}`,
                    })}
                    muiTableBodyCellProps={({ row, column }) => ({
                      'data-testid': `alarm-rule-view-triggered-alarm-cell-${row.original.id}-${column.id}`,
                    })}
                  />
                )}
              </div>
            </div>
            <div className={`tab-pane${activeTab === 1 ? ' active' : ''}`} id="alarm-rule-runs-panel" role="tabpanel"
              aria-labelledby="alarm-rule-runs-tab" data-testid="alarm-rule-view-rule-runs-panel">
              {runsActivated && (
                <div className="nav-content-custom" data-testid="alarm-rule-view-rule-runs-content">
                  <RuleRunsTable {...runs} active={activeTab === 1} $translate={$translate} formatTimestampFilter={formatTimestampFilter} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" onClick={close} data-testid="alarm-rule-view-close-button">
          <i className="fas fa-times fa-fw" aria-hidden="true" /> Close
        </button>
      </div>
    </div>
  );
};

AlarmRuleViewModal.propTypes = {
  rule: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  alarmRuleService: PropTypes.object.isRequired,
  accountService: PropTypes.object.isRequired,
  alarmNotificationService: PropTypes.object.isRequired,
  formatTimestampFilter: PropTypes.func.isRequired,
  $translate: PropTypes.func.isRequired,
};